import React, { useMemo, useState } from "react";
import {
  Box,
  Flex,
  Grid,
  Text,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerBody,
  useDisclosure,
} from "@chakra-ui/react";
import { FiChevronRight, FiChevronDown } from "react-icons/fi";
import { FaFingerprint } from "react-icons/fa";
import moment from "moment";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loading } from "../components/ui";
import { AppBar } from "../components/mobileUi";
import {
  DESK,
  DesktopBack,
  DesktopPage,
  DesktopPanel,
  DesktopSelect,
  DesktopTable,
  useIsDesktop,
} from "../components/desktopUi";
import { EMP_BASE_PATH } from "../config";
import {
  getAttendanceCalendarByMonthApi,
  getAttendanceSummaryByMonthApi,
} from "../api/attendance.api";
import {
  attendanceMeta,
  attendanceStatusText,
  typeLabel,
  formatCheckInPlace,
} from "../utils/attendance";

const PRIMARY = "#4081EB";
const CARD_BORDER = "rgba(8, 20, 45, 0.06)";
const TEXT = "#08142D";
const MUTED = "#94A3B8";

const DONUT_KEYS = [
  { key: "CHECKIN_ON_TIME", label: "Đi làm", color: "#18C758" },
  { key: "CHECKIN_LATE", label: "Đi muộn", color: "#1277EB" },
  { key: "HALF_SHIFT", label: "Nửa ca", color: "#8B5CF6" },
  { key: "MAKEUP_WORK", label: "Làm bù", color: "#10A7E2" },
  { key: "REGISTER_OUTSIDE", label: "Làm ngoài", color: "#F79D0D" },
  { key: "LEAVE", label: "Nghỉ có phép", color: "#EC5B2A" },
  { key: "ABSENT", label: "Vắng mặt", color: "#E75A90" },
  { key: "HOLIDAY", label: "Cuối tuần / Lễ", color: "#F45757" },
  { key: "MISSING", label: "Chưa chấm công", color: "#E8EEF6" },
];

const FILTER_OPTIONS = [
  { key: "ALL", label: "Tất cả" },
  { key: "CHECKIN_ON_TIME", label: "Đi làm" },
  { key: "CHECKIN_LATE", label: "Đi muộn" },
  { key: "HALF_SHIFT", label: "Nửa ca" },
  { key: "REGISTER_OUTSIDE", label: "Làm ngoài" },
  { key: "MAKEUP_WORK", label: "Làm bù" },
  { key: "LEAVE", label: "Nghỉ có phép" },
  { key: "ABSENT", label: "Vắng mặt" },
  { key: "HOLIDAY", label: "Cuối tuần / Lễ" },
  { key: "MISSING", label: "Chưa chấm công" },
];

const VN_DOW = ["Chủ Nhật", "Thứ Hai", "Thứ Ba", "Thứ Tư", "Thứ Năm", "Thứ Sáu", "Thứ Bảy"];
const dayHeader = (dateStr) => {
  const d = moment(dateStr);
  return `${VN_DOW[d.day()]}, ${d.format("DD/MM/YYYY")}`;
};
const timeHHmmss = (v) => (v ? moment(v).format("HH:mm:ss") : "-");

// ===== Donut (SVG) khớp DonutSummary của app =====
const polar = (cx, cy, r, ang) => {
  const rad = ((ang - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
};
const arcPath = (cx, cy, r, start, end) => {
  const s = polar(cx, cy, r, end);
  const e = polar(cx, cy, r, start);
  const large = end - start <= 180 ? "0" : "1";
  return ["M", s.x, s.y, "A", r, r, 0, large, 0, e.x, e.y].join(" ");
};

const Donut = ({ items, total, size = 136, stroke = 30, gap = 2, startAngle = -90 }) => {
  const r = (size - stroke) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const arcs = [];
  const positive = items.filter((it) => it.value > 0);
  const sum = positive.reduce((s, it) => s + it.value, 0);
  let cursor = startAngle;
  if (sum > 0) {
    positive.forEach((it) => {
      const sweep = (it.value / sum) * 360;
      const start = cursor + gap / 2;
      const end = Math.max(start, cursor + sweep - gap / 2);
      cursor += sweep;
      arcs.push({ key: it.key, color: it.color, d: arcPath(cx, cy, r, start, end) });
    });
  }
  return (
    <svg width={size} height={size}>
      <circle cx={cx} cy={cy} r={r} stroke="#FFFFFF" strokeWidth={stroke} fill="none" />
      {arcs.map((a) => (
        <path key={a.key} d={a.d} stroke={a.color} strokeWidth={stroke} fill="none" />
      ))}
    </svg>
  );
};

/**
 * Nội dung tổng hợp chấm công theo tháng của người đang đăng nhập.
 * Tách riêng để dùng lại giữa màn "Chi tiết chấm công" và tab "Số công"
 * trong "Chi tiết KPI cá nhân" — khớp AttendanceSummaryContent của app.
 * @param month chuỗi "YYYY-MM"
 */
export const AttendanceSummaryContent = ({ month }) => {
  const mMonth = moment(month, "YYYY-MM");
  const dateParam = mMonth.format("MM-YYYY");

  const isDesktop = useIsDesktop();
  const [filter, setFilter] = useState("ALL");
  const [expanded, setExpanded] = useState({});
  const drawer = useDisclosure();

  const calQuery = useQuery({
    queryKey: ["emp", "calendar", dateParam],
    queryFn: () => getAttendanceCalendarByMonthApi(dateParam),
  });

  const typeForApi = filter === "MISSING" ? "ALL" : filter;
  const listQuery = useQuery({
    queryKey: ["emp", "summary", dateParam, typeForApi],
    queryFn: () => getAttendanceSummaryByMonthApi(dateParam, typeForApi),
  });

  const legendCounts = calQuery.data?.legendCounts || {};
  const total = calQuery.data?.totalWorkingDayRate ?? 0;

  const donutItems = useMemo(
    () => DONUT_KEYS.map((k) => ({ ...k, value: Number(legendCounts?.[k.key] ?? 0) })),
    [legendCounts]
  );
  const donutSum = donutItems.reduce((s, it) => s + it.value, 0);

  // Dựng danh sách theo ngày (kèm ngày "Chưa chấm công")
  const grouped = useMemo(() => {
    const items = listQuery.data?.items || listQuery.data || [];
    const recMap = new Map();
    (Array.isArray(items) ? items : []).forEach((it) => {
      const k = moment(it.date).format("YYYY-MM-DD");
      const arr = recMap.get(k) || [];
      arr.push(it);
      recMap.set(k, arr);
    });

    const today = moment().startOf("day");
    const start = mMonth.clone().startOf("month");
    const end = mMonth.clone().endOf("month");
    const rows = [];
    for (const d = start.clone(); d.isSameOrBefore(end, "day"); d.add(1, "day")) {
      if (d.isAfter(today, "day")) continue;
      const k = d.format("YYYY-MM-DD");
      const recs = recMap.get(k);
      if (recs && recs.length) {
        recs.forEach((r, i) =>
          rows.push({ kind: "RECORD", id: `${r._id || k}-${i}`, dateKey: k, data: r })
        );
      } else {
        rows.push({ kind: "MISSING", id: `missing-${k}`, dateKey: k });
      }
    }
    rows.sort((a, b) => (a.dateKey > b.dateKey ? -1 : 1));

    let filtered = rows;
    if (filter === "MISSING") filtered = rows.filter((r) => r.kind === "MISSING");
    else if (filter !== "ALL")
      filtered = rows.filter((r) => r.kind === "RECORD" && r.data.attendanceType === filter);

    return filtered;
  }, [listQuery.data, filter, month]); // eslint-disable-line react-hooks/exhaustive-deps

  const filterLabel = FILTER_OPTIONS.find((o) => o.key === filter)?.label || "Tất cả";

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    const rows = grouped.map((row) => {
      if (row.kind === "MISSING") {
        return {
          id: row.id,
          date: dayHeader(row.dateKey),
          status: "Chưa chấm công",
          statusColor: MUTED,
          type: "-",
          rate: "0",
          time: "-",
          place: "-",
          note: "-",
        };
      }
      const it = row.data;
      const meta = attendanceMeta(it.attendanceType);
      return {
        id: row.id,
        date: dayHeader(row.dateKey),
        status: attendanceStatusText(it.attendanceType),
        statusColor: meta.color,
        type:
          it.attendanceType === "HOLIDAY"
            ? it.holiday?.name || "Ngày nghỉ cuối tuần"
            : typeLabel(it.attendanceType),
        rate: typeof it.workingDayRate === "number" ? String(it.workingDayRate) : "0",
        time: timeHHmmss(it.checkinTime),
        place: formatCheckInPlace(it) || "-",
        note: it?.note || "-",
      };
    });

    return (
      <Flex direction="column" gap="16px">
        <DesktopPanel>
          {calQuery.isLoading ? (
            <Loading label="Đang tải..." />
          ) : (
            <Flex align="center" gap="32px" flexWrap="wrap">
              <Flex align="center" gap="20px">
                <Donut items={donutItems} total={donutSum} size={160} stroke={34} />
                <Box>
                  <Text fontSize="12px" color={MUTED} letterSpacing="0.2px" fontWeight={600}>
                    TỔNG CÔNG THÁNG
                  </Text>
                  <Flex align="baseline" mt="4px">
                    <Text fontSize="32px" fontWeight={800} color={PRIMARY} lineHeight="36px">
                      {total}
                    </Text>
                    <Text fontSize="14px" fontWeight={600} color={MUTED} ml="7px">
                      Công
                    </Text>
                  </Flex>
                </Box>
              </Flex>

              <Grid
                flex="1"
                minW="360px"
                templateColumns="repeat(auto-fill, minmax(220px, 1fr))"
                gap="10px"
              >
                {donutItems.map((it) => {
                  const pct = donutSum > 0 ? Math.round((it.value / donutSum) * 100) : 0;
                  return (
                    <Flex key={it.key} align="center" gap="8px">
                      <Box
                        w="16px"
                        h="16px"
                        borderRadius="5px"
                        bg={it.color}
                        border="1px solid"
                        borderColor={CARD_BORDER}
                        flexShrink={0}
                      />
                      <Text fontSize="13px" color={TEXT}>
                        {it.label}:
                      </Text>
                      <Text fontSize="13px" color={MUTED}>
                        {it.value}-{pct}%
                      </Text>
                    </Flex>
                  );
                })}
              </Grid>
            </Flex>
          )}
        </DesktopPanel>

        <DesktopPanel p="0">
          <Flex align="center" justify="space-between" gap="16px" p="16px" flexWrap="wrap">
            <Text fontSize="16px" fontWeight={600} color={DESK.title}>
              Thông tin chi tiết
            </Text>
            <Box w="220px">
              <DesktopSelect
                options={FILTER_OPTIONS.filter((o) => o.key !== "ALL").map((o) => ({
                  value: o.key,
                  label: o.label,
                }))}
                value={filter === "ALL" ? "" : filter}
                placeholder="Tất cả"
                onChange={(v) => {
                  setFilter(v || "ALL");
                  setExpanded({});
                }}
              />
            </Box>
          </Flex>

          <DesktopTable
            loading={listQuery.isLoading}
            headers={[
              { key: "date", label: "Ngày", align: "left", width: "22%" },
              {
                key: "status",
                label: "Trạng thái",
                width: "14%",
                render: (row) => (
                  <Text fontSize="14px" fontWeight={600} color={row.statusColor}>
                    {row.status}
                  </Text>
                ),
              },
              { key: "type", label: "Phân loại", width: "14%" },
              { key: "rate", label: "Số công", width: "8%" },
              { key: "time", label: "Giờ chấm công", width: "12%" },
              { key: "place", label: "Địa điểm", align: "left", width: "18%" },
              { key: "note", label: "Ghi chú", align: "left", width: "12%" },
            ]}
            rows={rows}
          />
        </DesktopPanel>
      </Flex>
    );
  }

  return (
      <Box px="16px" pt="12px">
      {/* ===== Donut ===== */}
      <Box bg="white" borderRadius="8px" border="1px solid" borderColor={CARD_BORDER} p="12px">
        {calQuery.isLoading ? (
          <Loading label="Đang tải..." />
        ) : (
          <>
            <Flex align="center" justify="space-between">
              <Box w="50%" display="flex" justifyContent="center">
                <Donut items={donutItems} total={donutSum} />
              </Box>
              <Box flex="1">
                <Text fontSize="12px" color={MUTED} letterSpacing="0.2px" fontWeight={600}>
                  TỔNG CÔNG THÁNG
                </Text>
                <Flex align="baseline" mt="4px">
                  <Text fontSize="26px" fontWeight={800} color={PRIMARY} lineHeight="30px">
                    {total}
                  </Text>
                  <Text fontSize="14px" fontWeight={600} color={MUTED} ml="7px">
                    Công
                  </Text>
                </Flex>
              </Box>
            </Flex>

            <Box h="1px" bg="#E1E1E1" my="12px" />

            <Grid templateColumns="repeat(2, 1fr)" gap="8px">
              {donutItems.map((it) => {
                const pct = donutSum > 0 ? Math.round((it.value / donutSum) * 100) : 0;
                return (
                  <Flex key={it.key} align="center" gap="8px">
                    <Box w="16px" h="16px" borderRadius="5px" bg={it.color} border="1px solid" borderColor={CARD_BORDER} flexShrink={0} />
                    <Text fontSize="13px" color={TEXT}>
                      {it.label}:
                    </Text>
                    <Text fontSize="13px" color={MUTED}>
                      {it.value}-{pct}%
                    </Text>
                  </Flex>
                );
              })}
            </Grid>
          </>
        )}
      </Box>

      {/* ===== Tiêu đề + bộ lọc ===== */}
      <Flex align="center" justify="space-between" mt="12px">
        <Text fontSize="15px" fontWeight={600} color={TEXT}>
          Thông tin chi tiết
        </Text>
        <Flex
          as="button"
          onClick={drawer.onOpen}
          align="center"
          gap="8px"
          h="34px"
          px="12px"
          borderRadius="999px"
          bg="#F5F7FB"
          border="1px solid"
          borderColor={CARD_BORDER}
        >
          <Text fontSize="14px" color={TEXT}>{filterLabel}</Text>
          <Box as={FiChevronDown} fontSize="18px" color={TEXT} />
        </Flex>
      </Flex>

      {/* ===== Danh sách ===== */}
      <Flex direction="column" gap="10px" mt="10px">
        {listQuery.isLoading ? (
          <Loading label="Đang tải..." />
        ) : (
          grouped.map((row) => {
            const header = dayHeader(row.dateKey);
            if (row.kind === "MISSING") {
              return (
                <Flex key={row.id} bg="white" borderRadius="14px" border="1px solid" borderColor={CARD_BORDER} p="12px" align="center" gap="12px">
                  <Flex w="44px" h="44px" borderRadius="10px" bg="#EAF2FF" align="center" justify="center" flexShrink={0}>
                    <Box as={FaFingerprint} fontSize="22px" color="#18212F" />
                  </Flex>
                  <Box flex="1">
                    <Text fontSize="14px" fontWeight={600} color={TEXT}>{header}</Text>
                    <Text fontSize="13px" color={MUTED} mt="4px">Chưa chấm công</Text>
                  </Box>
                  <Box as={FiChevronRight} fontSize="18px" color={MUTED} />
                </Flex>
              );
            }
            const it = row.data;
            const meta = attendanceMeta(it.attendanceType);
            const isOpen = !!expanded[row.id];
            return (
              <Box key={row.id} bg="white" borderRadius="14px" border="1px solid" borderColor={CARD_BORDER} p="12px">
                <Flex
                  as="button"
                  w="100%"
                  align="center"
                  gap="12px"
                  onClick={() => setExpanded((p) => ({ ...p, [row.id]: !p[row.id] }))}
                >
                  <Flex w="44px" h="44px" borderRadius="10px" bg={meta.bg} align="center" justify="center" flexShrink={0}>
                    <Box as={FaFingerprint} fontSize="22px" color={meta.color} />
                  </Flex>
                  <Box flex="1" textAlign="left">
                    <Text fontSize="14px" fontWeight={600} color={TEXT}>{header}</Text>
                    <Flex align="center" gap="6px" mt="4px">
                      <Box as={FiCheckIcon} color={meta.color} />
                      <Text fontSize="13px" color={meta.color} fontWeight={500}>
                        {attendanceStatusText(it.attendanceType)}
                      </Text>
                    </Flex>
                  </Box>
                  <Box as={isOpen ? FiChevronDown : FiChevronRight} fontSize="18px" color={MUTED} />
                </Flex>

                {isOpen && (
                  <Flex direction="column" gap="10px" pt="10px">
                    <KV label="Phân loại" value={it.attendanceType === "HOLIDAY" ? it.holiday?.name || "Ngày nghỉ cuối tuần" : typeLabel(it.attendanceType)} />
                    <KV label="Số công" value={typeof it.workingDayRate === "number" ? String(it.workingDayRate) : "0"} />
                    <KV label="Thời gian chấm công" value={timeHHmmss(it.checkinTime)} />
                    <KV label="Địa điểm chấm công" value={formatCheckInPlace(it) || "-"} />
                    <KV label="Ghi chú" value={it?.note || "-"} />
                  </Flex>
                )}
              </Box>
            );
          })
        )}
        {!listQuery.isLoading && grouped.length === 0 && (
          <Text fontSize="14px" color={MUTED} textAlign="center" py="20px">
            Không có dữ liệu
          </Text>
        )}
      </Flex>

      {/* ===== Drawer bộ lọc ===== */}
      <Drawer isOpen={drawer.isOpen} onClose={drawer.onClose} placement="bottom">
        <DrawerOverlay bg="rgba(0,0,0,0.5)" />
        <DrawerContent borderTopRadius="16px" maxW="480px" mx="auto">
          <DrawerBody py="16px" px="16px">
            <Text fontSize="16px" fontWeight={700} color={TEXT} mb="8px">
              Phân loại công
            </Text>
            {FILTER_OPTIONS.map((o, i) => {
              const active = o.key === filter;
              const isLast = i === FILTER_OPTIONS.length - 1;
              return (
                <Box
                  key={o.key}
                  as="button"
                  w="100%"
                  textAlign="left"
                  py="12px"
                  borderBottom={isLast ? "none" : "1px solid #EEF1F6"}
                  onClick={() => {
                    setFilter(o.key);
                    setExpanded({});
                    drawer.onClose();
                  }}
                >
                  <Text fontSize="15px" color={active ? PRIMARY : TEXT} fontWeight={active ? 600 : 400}>
                    {o.label}
                  </Text>
                </Box>
              );
            })}
            <Flex as="button" onClick={drawer.onClose} mt="12px" w="100%" h="46px" align="center" justify="center" borderRadius="10px" bg={PRIMARY} color="white" fontWeight={700}>
              Đóng
            </Flex>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
      </Box>
  );
};

const AttendanceSummary = () => {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const [params] = useSearchParams();
  const month = params.get("month") || moment().format("YYYY-MM"); // YYYY-MM
  const mMonth = moment(month, "YYYY-MM");

  if (isDesktop) {
    return (
      <DesktopPage
        title={`Chi tiết chấm công tháng ${mMonth.format("MM/YYYY")}`}
        subtitle=""
        right={
          <DesktopBack onClick={() => navigate(`${EMP_BASE_PATH}/chuyen-can`)} />
        }
      >
        <AttendanceSummaryContent month={month} />
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" pb="24px">
      <AppBar
        title={`Tháng ${mMonth.format("MM/YYYY")} - Chấm công`}
        onBack={() => navigate(`${EMP_BASE_PATH}/chuyen-can`)}
      />
      <AttendanceSummaryContent month={month} />
    </Box>
  );
};

// nhỏ: dòng key-value trong phần chi tiết
const KV = ({ label, value }) => (
  <Flex gap="12px">
    <Text fontSize="14px" color={MUTED} flex="1">{label}</Text>
    <Text fontSize="14px" color={TEXT} flex="1" textAlign="right" whiteSpace="pre-line">{value}</Text>
  </Flex>
);

// icon check tròn nhỏ trước status
const FiCheckIcon = (props) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" {...props}>
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
    <path d="M8 12l2.5 2.5L16 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default AttendanceSummary;
